import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { AccessibilityControls } from './components/AccessibilityControls';
import {
  A11Y_STORAGE_KEY,
  getInitialA11yScheme,
  getInitialHoverZoomOthers,
  syncHoverZoomOthersToDom,
} from './accessibility/a11yPreferences';
import { useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { localizationService } from './services/localizationService';
import { adminServices } from './services/adminServices';
import { getRenewalMarketQuote } from './services/pricingRules';
import {
  billingLevelForPortal,
  cycleIsFinalised,
  higherAccreditationLevels,
} from './utils/renewalCycleLogic';
import { languageManifest as baseLanguageManifest } from './locales';
import { countryEndonyms, countryEnglishLabel } from './locales/countries';
import { useManagedLanguages } from './hooks/useManagedLanguages';
import { en as enMaster } from './locales/en';
import { getTranslatingIndicator } from './locales/translatingIndicator';
import SupportHelpButton from './components/SupportHelpButton';
import SupportIssueModal from './components/SupportIssueModal';
import './app.css';
import './admin-styles.css';
import './school-portal.css';
import './renewal-page.css';

/** Flat string map for `localizationService.sync(langKey, 'renewal_page_v1', ...)`. */
const RENEWAL_PAGE_EN = {
  page_title: 'Renewal or upgrade accreditation',
  page_intro:
    'For schools that have finished a cycle: renew at the same level, or move up. Your issued report stays in My Reports. You do not register again.',
  section_form_title: 'Request details',
  section_form_intro: 'Say who is applying, confirm the three declarations, then pay. That opens the next cycle.',
  renewal_intent_label: 'Your request',
  renew_my_current_level: 'Renew my current level ({{level}})',
  upgrade_my_accreditation: 'Upgrade my accreditation',
  missing_sid_title: 'School portal required',
  missing_sid_body:
    'Renewal and upgrades are started from your school portal: open the portal with the magic link IFIP emailed to you, then use the renewal option on your dashboard. This page needs your school id in the address bar, which the portal supplies when you open renewal from there.',
  missing_sid_recovery_hint:
    'If you have lost your portal link, open the registration page and use Account access recovery (or use the direct link below).',
  missing_sid_contact: 'For all other support or enquiries, please contact',
  missing_sid_contact_email: 'info@ifip.group',
  link_register_recovery: 'Open account recovery',
  link_register_intro: 'Registration page (intro)',
  link_back_portal: 'Back to portal',

  request_type_label: 'Application type',
  request_type_renew: 'Renew at current level',
  request_type_upgrade: 'Upgrade to a higher level',

  target_level_label: 'Target accreditation level for this upgrade',
  level_foundation: 'Foundation',
  level_certified: 'Certified',
  level_global: 'Global Accreditation',

  label_submitter_name: 'Name of person submitting this request',
  label_submission_date: 'Date of this request',

  consent_q21_label: 'Institutional authorization',
  consent_q21_desc:
    'I confirm that I have the authority to submit this request on behalf of the named organisation.',
  consent_q22_label: 'Accuracy & truthfulness',
  consent_q22_desc:
    'I declare that the information provided is accurate and true to the best of my knowledge.',
  consent_q23_label: 'Data processing agreement',
  consent_q23_desc:
    'I agree to IFIP’s data handling policy, acknowledging that this information will be used for accreditation assessment and institutional profiling.',

  visit_in_person_intro:
    'The selected level of accreditation involves an in-person visit to the school. Visit dates are set by IFIP; please select your preferred window below.',
  visit_window_label: 'Preferred visit window',
  visit_window_hint:
    'IFIP will propose dates within this window where possible. Dates are subject to programme capacity.',
  visit_window_1_3: 'Within 1–3 months',
  visit_window_3_6: 'Within 3–6 months',
  visit_window_6_12: 'Within 6–12 months',
  visit_window_flexible: 'Flexible — contact me to discuss',

  upgrade_warning_title: 'Upgrade request',
  upgrade_warning_body:
    'You are requesting a change while a cycle may still be active. This creates a separate paid request. Please confirm below.',
  upgrade_confirm_label: 'I understand and wish to proceed with an upgrade request.',

  renewal_blocked_title: 'Not available yet',
  renewal_blocked_body:
    'A renewal or an upgrade can start only once the current accreditation cycle is complete. Finish this cycle first, then come back.',
  partner_pay_title: 'Pay your channel partner',
  partner_pay_body:
    'Payment in {{country}} is made with our channel partner {{partnerName}}. When payment is completed, please enter the code given to you by {{partnerName}} here.',
  partner_pay_body_no_name:
    'Payment in {{country}} is made with our channel partner. When payment is completed, please enter the code they gave you here.',
  partner_code_label: 'Proof-of-purchase code',
  partner_code_label_named: 'Code from {{partnerName}}',
  online_pay_body:
    'Payment in {{country}} is taken directly by IFIP through Stripe. Pay by card now, or request an invoice.',
  closed_market_body:
    'This level is not open for your country yet. Please contact IFIP and we will arrange the next step.',
  success_partner:
    'The new cycle is open. Go back to your portal and choose whether to keep your previous self-evaluation or start from a blank form. Your issued report stays in My Reports.',
  preview_banner:
    'Preview only. Nothing on this page is saved, and no payment is taken.',

  payment_section_title: 'Payment',
  payment_method_label: 'Payment method',
  payment_card: 'Pay now (Stripe — card, Apple Pay, Google Pay where available)',
  payment_invoice: 'Request invoice',
  fee_label: 'Accreditation fee',
  fee_unavailable: '—',
  fee_bespoke:
    'This combination of country and level is not available for automated billing. Please contact IFIP to discuss options.',
  payment_terms_summary: 'Payment terms & fees',
  payment_terms_detail:
    'Where possible, your fee is shown in local currency with the GBP amount in brackets; if Stripe cannot charge in that currency for your account, checkout falls back to GBP. Fees are not refundable if IFIP recommends awarding a lower accreditation level than the one you requested; by continuing you accept this policy.\n\nOnline payments are processed securely by Stripe (card, Apple Pay, Google Pay, and other methods shown at checkout). If you request an invoice, IFIP finance will issue it separately; access to paid services may follow confirmed settlement.',
  label_agree_terms: "I have read and agree to IFIP's terms and conditions",

  btn_continue_payment: 'Save details and continue to payment',
  btn_confirm_code: 'Confirm code and open the portal',
  btn_working: 'Working…',
  error_validation: 'Please complete all required fields and confirmations.',
  error_country: 'Country is missing from your school profile. Please contact IFIP to update your record.',
  success_checkout: 'Checkout opened in a new tab. You can return to your portal when finished.',
  success_invoice: 'Invoice request submitted. IFIP finance will follow up.',

  localizing: 'Loading…',

  a11y_btn: 'Accessibility',
  a11y_panel_title: 'Colour scheme',
  a11y_scheme_default: 'Default theme',
  a11y_scheme_wb: 'White text on black',
  a11y_scheme_cg: 'Black text on white',
  a11y_scheme_nb: 'Navy on pale blue',
  a11y_scheme_sol: 'Teal on cream',
  help_btn: 'Help'
};

const stripLocalizationMeta = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => k !== 'lastUpdated' && !k.startsWith('_source_'))
  );
};

const normalizeTier = (raw) => {
  const k = (raw == null ? '' : String(raw)).toLowerCase();
  if (k === 'accredited') return 'global';
  return k;
};

/** Pricing / Stripe use `accredited`; portal UI uses `global`. */
const billingLevelKey = (portalLevel) => billingLevelForPortal(portalLevel);

const levelDisplayName = (portalLevel, strings) => {
  const k = normalizeTier(portalLevel);
  if (k === 'certified') return strings.level_certified;
  if (k === 'global') return strings.level_global;
  return strings.level_foundation;
};

function resolvePreviewCountry(raw) {
  const value = String(raw || '').trim();
  if (!value) return 'United Kingdom';
  const match = countryEndonyms.find(
    (entry) => entry === value || countryEnglishLabel(entry).toLowerCase() === value.toLowerCase()
  );
  return match || value;
}

function fillRenewalTemplate(template, values) {
  return String(template || '').replace(/\{\{(\w+)\}\}/g, (_, key) => (
    values[key] != null ? String(values[key]) : ''
  ));
}

const todayIsoDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const RenewalUpgradePage = () => {
  const { languageManifest } = useManagedLanguages();
  const [searchParams] = useSearchParams();
  const preview = searchParams.get('preview') === '1';
  const previewMarketParam = searchParams.get('previewMarket');
  const previewMarket = ['online', 'partner', 'closed'].includes(previewMarketParam)
    ? previewMarketParam
    : 'online';
  const previewFinalised = searchParams.get('previewFinalised') !== '0';
  const previewCountry = resolvePreviewCountry(searchParams.get('previewCountry'));
  const schoolId = preview ? 'preview' : searchParams.get('sid');

  const getInitialLang = () => {
    const saved = localStorage.getItem('ifip_lang');
    if (saved && baseLanguageManifest[saved]) return saved;
    const browserLang = navigator.language.split('-')[0];
    if (baseLanguageManifest[browserLang]) return browserLang;
    return 'en';
  };

  const [langKey, setLangKey] = useState(getInitialLang);
  const [ui, setUi] = useState(RENEWAL_PAGE_EN);
  const [portalUi, setPortalUi] = useState(() => ({ ...enMaster.ui }));
  const [isTranslating, setIsTranslating] = useState(false);
  const [schoolProfile, setSchoolProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  /** `renew` | `upgrade` — maps to Firestore requestType below */
  const [intent, setIntent] = useState('renew');
  const [targetLevel, setTargetLevel] = useState('foundation');
  const [submitterName, setSubmitterName] = useState('');
  const [submissionDate, setSubmissionDate] = useState(todayIsoDate());
  const [cAuth, setCAuth] = useState(false);
  const [cAcc, setCAcc] = useState(false);
  const [cData, setCData] = useState(false);
  const [offerCode, setOfferCode] = useState('');

  const [pricingConfig, setPricingConfig] = useState(null);
  const [paymentChoice, setPaymentChoice] = useState('card');
  const [feeServerQuote, setFeeServerQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [a11yPanelOpen, setA11yPanelOpen] = useState(false);
  const [a11yScheme, setA11yScheme] = useState(getInitialA11yScheme);
  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    try {
      const persisted = a11yScheme === 'bw' ? 'cg' : a11yScheme;
      localStorage.setItem(
        A11Y_STORAGE_KEY,
        JSON.stringify({ colorScheme: persisted, hoverZoomOthers: getInitialHoverZoomOthers() })
      );
      syncHoverZoomOthersToDom(getInitialHoverZoomOthers());
    } catch {
      // Ignore persistence failures.
    }
    document.documentElement.setAttribute('data-a11y-scheme', a11yScheme === 'bw' ? 'cg' : a11yScheme);
  }, [a11yScheme]);

  useEffect(() => {
    if (preview) {
      setSchoolProfile({
        officialName: 'Northbridge Preview School',
        officialEmail: 'lead@northbridge.example',
        accreditationLevel: 'foundation',
        workflowStep: previewFinalised ? 'finalised' : 'fact_check',
        officialAddressCountry: previewCountry,
      });
      setProfileLoaded(true);
      return undefined;
    }
    if (!schoolId) return undefined;
    const ref = doc(db, 'schools', schoolId);
    return onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setSchoolProfile(snap.data());
      } else {
        setSchoolProfile(null);
      }
      setProfileLoaded(true);
    });
  }, [schoolId, preview, previewFinalised, previewCountry]);

  useEffect(() => {
    let cancelled = false;
    adminServices.getPricingConfig().then((cfg) => {
      if (!cancelled) setPricingConfig(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const syncAll = async () => {
      setIsTranslating(true);
      try {
        const [storedRenewal, storedMain] = await Promise.all([
          localizationService.getStored(langKey, 'renewal_page_v1'),
          localizationService.getStored(langKey, 'app_main')
        ]);
        if (cancelled) return;
        if (storedRenewal) setUi({ ...RENEWAL_PAGE_EN, ...stripLocalizationMeta(storedRenewal) });
        if (storedMain) setPortalUi({ ...enMaster.ui, ...stripLocalizationMeta(storedMain) });

        const [renewalSynced, mainSynced] = await Promise.all([
          localizationService.sync(langKey, 'renewal_page_v1', RENEWAL_PAGE_EN),
          localizationService.sync(langKey, 'app_main', enMaster.ui)
        ]);
        if (cancelled) return;
        if (renewalSynced) setUi({ ...RENEWAL_PAGE_EN, ...stripLocalizationMeta(renewalSynced) });
        if (mainSynced) setPortalUi(stripLocalizationMeta(mainSynced));
      } catch (e) {
        console.error('Renewal page localization:', e);
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    };
    syncAll();
    return () => {
      cancelled = true;
    };
  }, [langKey]);

  const country = useMemo(() => {
    if (!schoolProfile) return '';
    return (
      schoolProfile.officialAddressCountry ||
      schoolProfile.answers?.officialAddressCountry ||
      schoolProfile.answers?.country ||
      ''
    );
  }, [schoolProfile]);

  const officialName = schoolProfile?.officialName || schoolId || '';
  const officialEmail =
    schoolProfile?.officialEmail ||
    schoolProfile?.answers?.officialEmail ||
    schoolProfile?.answers?.q3 ||
    '';

  const workflowStep = String(schoolProfile?.workflowStep || '').toLowerCase();
  const cycleComplete = cycleIsFinalised(workflowStep);
  const requestType = intent === 'renew' ? 'renew_same_level' : 'upgrade_level';
  const dir = languageManifest[langKey]?.dir || 'ltr';

  const currentLevelKey = useMemo(() => {
    if (!schoolProfile) return 'foundation';
    const cur = normalizeTier(schoolProfile.accreditationLevel || 'foundation');
    return ['foundation', 'certified', 'global'].includes(cur) ? cur : 'foundation';
  }, [schoolProfile]);

  const higherLevels = useMemo(
    () => higherAccreditationLevels(currentLevelKey),
    [currentLevelKey]
  );
  const canUpgrade = cycleComplete && higherLevels.length > 0;

  useEffect(() => {
    if (!schoolProfile) return;
    if (intent === 'renew' || !canUpgrade) setTargetLevel(currentLevelKey);
  }, [schoolProfile, intent, currentLevelKey, canUpgrade]);

  useEffect(() => {
    if (intent === 'upgrade' && higherLevels.length && !higherLevels.includes(targetLevel)) {
      setTargetLevel(higherLevels[0]);
    }
  }, [intent, higherLevels, targetLevel]);

  const feeDerived = useMemo(() => {
    if (!country || !pricingConfig) return null;
    return getRenewalMarketQuote(country, billingLevelKey(targetLevel), pricingConfig);
  }, [country, targetLevel, pricingConfig]);

  const paymentMode = preview ? previewMarket : (feeDerived?.mode || 'closed');
  const partnerName = useMemo(() => {
    const named = (feeDerived?.partnerName || '').trim();
    if (named) return named;
    if (!preview || previewMarket !== 'partner' || !country || !pricingConfig) return '';
    for (const level of ['foundation', 'certified', 'accredited']) {
      const quote = getRenewalMarketQuote(country, level, pricingConfig);
      const found = (quote.partnerName || '').trim();
      if (quote.mode === 'partner' && found) return found;
    }
    return '';
  }, [feeDerived, preview, previewMarket, country, pricingConfig]);
  const countryLabel = countryEnglishLabel(country);

  useEffect(() => {
    let cancelled = false;
    if (!country || !feeDerived?.available) {
      setFeeServerQuote(null);
      return undefined;
    }
    const level = billingLevelKey(targetLevel);
    (async () => {
      try {
        const data = await adminServices.getAccreditationFeeQuote(country, level);
        if (!cancelled) setFeeServerQuote(data);
      } catch {
        if (!cancelled) {
          setFeeServerQuote({
            displayLine: `£${Number(feeDerived.amount).toLocaleString('en-GB')}`
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [country, targetLevel, feeDerived?.available, feeDerived?.amount]);

  const validate = () => {
    if (!cycleComplete) return false;
    if (!submitterName.trim()) return false;
    if (!submissionDate) return false;
    if (!cAuth || !cAcc || !cData) return false;
    if (requestType === 'upgrade_level' && !higherLevels.includes(targetLevel)) return false;
    if (paymentMode === 'closed') return false;
    if (paymentMode === 'partner' && !offerCode.trim()) return false;
    if (!preview && feeDerived && feeDerived.available === false) return false;
    return true;
  };

  const renewOptionLabel = ui.renew_my_current_level.replace(
    /\{\{level\}\}/g,
    levelDisplayName(currentLevelKey, ui)
  );

  const portalHref = `/portal?sid=${encodeURIComponent(schoolId || '')}`;
  const supportContext = {
    route: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/renewal',
    actorType: 'renewal_user',
    schoolId,
    schoolName: officialName,
    reporterEmail: officialEmail || null,
    workflowStep: workflowStep || null,
    language: langKey,
    intent,
    targetLevel,
    paymentChoice,
  };

  const pickRenewalA11yLabels = () => ({
    btn: ui.a11y_btn || RENEWAL_PAGE_EN.a11y_btn || enMaster.ui.a11y_btn,
    panelTitle: ui.a11y_panel_title || RENEWAL_PAGE_EN.a11y_panel_title || enMaster.ui.a11y_panel_title,
    panelAria: ui.a11y_panel_title || RENEWAL_PAGE_EN.a11y_panel_title || enMaster.ui.a11y_panel_title,
    schemeDefault: ui.a11y_scheme_default || RENEWAL_PAGE_EN.a11y_scheme_default || enMaster.ui.a11y_scheme_default,
    schemeWb: ui.a11y_scheme_wb || RENEWAL_PAGE_EN.a11y_scheme_wb || enMaster.ui.a11y_scheme_wb,
    schemeCg: ui.a11y_scheme_cg || RENEWAL_PAGE_EN.a11y_scheme_cg || enMaster.ui.a11y_scheme_cg,
    schemeNb: ui.a11y_scheme_nb || RENEWAL_PAGE_EN.a11y_scheme_nb || enMaster.ui.a11y_scheme_nb,
    schemeSol: ui.a11y_scheme_sol || RENEWAL_PAGE_EN.a11y_scheme_sol || enMaster.ui.a11y_scheme_sol,
  });

  const renewalSplashLangAria =
    portalUi.nav_lang_select ||
    portalUi.grid_labels?.language ||
    ui.nav_lang_select ||
    enMaster.ui.nav_lang_select ||
    'Language';

  const renderPortalHeader = (nameFallback) => (
    <header className="portal-header renewal-portal-header">
      <div className="renewal-portal-header__inner">
        <div className="renewal-portal-header__brand">
          <a
            href={portalHref}
            className="btn-back-dashboard"
            style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit' }}
          >
            <span style={{ flexShrink: 0 }}>←</span>
            {ui.link_back_portal}
          </a>
          <img src="/images/01%20IFIP%20Logo.png" alt="IFIP Logo" style={{ height: '80px', width: 'auto' }} />
          <div className="renewal-portal-header__titles">
            <h1 style={{ margin: 0, fontSize: '2.5rem', color: 'var(--a11y-page-text)', opacity: 0.95, lineHeight: 1.1 }}>
              {portalUi.portal_title || enMaster.ui.portal_title}:{' '}
              <span style={{ color: '#138a8a', fontWeight: 800 }}>{nameFallback}</span>
            </h1>
            <p style={{ margin: '5px 0 0 0', fontSize: '1.2rem', color: 'var(--a11y-page-text)', opacity: 0.65, fontWeight: 400, letterSpacing: '0.5px' }}>
              {portalUi.portal_subtitle || enMaster.ui.portal_subtitle}
            </p>
          </div>
        </div>
        <div className="renewal-header-tools">
          <div className="renewal-header-tools__row renewal-header-tools__row--chrome">
            <AccessibilityControls
              panelOpen={a11yPanelOpen}
              setPanelOpen={setA11yPanelOpen}
              scheme={a11yScheme}
              setScheme={setA11yScheme}
              labels={pickRenewalA11yLabels()}
              rootClassName="ifip-a11y-control-root ifip-a11y-control-root--splash-inline-toolbar"
              panelId="a11y-panel-renewal"
              accessibilityTriggerIconOnly
              splashLangSelect={
                <select
                  className="ifip-splash-lang-select"
                  value={langKey}
                  onChange={(e) => {
                    setLangKey(e.target.value);
                    localStorage.setItem('ifip_lang', e.target.value);
                  }}
                  aria-label={renewalSplashLangAria}
                >
                  {Object.entries(languageManifest)
                    .sort((a, b) =>
                      (a[1].nativeName || a[1].label || a[0]).localeCompare(
                        b[1].nativeName || b[1].label || b[0]
                      )
                    )
                    .map(([k, lang]) => (
                      <option key={k} value={k}>
                        {lang.nativeName}
                      </option>
                    ))}
                </select>
              }
            />
          </div>
          <div className="renewal-header-tools__row renewal-header-tools__row--meta">
            <div className="renewal-header-tools__translating-slot" aria-live="polite">
              {isTranslating ? (
                <span
                  className={`renewal-translating ${langKey === 'en' ? 'translating-dots' : 'translating-dots translating-dots--plain'}`}
                  style={{ letterSpacing: langKey === 'en' ? '1px' : '0.02em' }}
                >
                  {getTranslatingIndicator(langKey)}
                </span>
              ) : null}
            </div>
            <a
              href={`mailto:support@ifip.group?subject=Translation%20Concern%3A%20${encodeURIComponent(schoolId || '')}`}
              className="btn-nudge"
              style={{
                fontSize: '0.7rem',
                textDecoration: 'none',
                padding: '4px 10px',
                background: 'rgba(255,255,255,0.05)',
                color: 'white',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.1)',
                flexShrink: 0
              }}
            >
              {portalUi.btn_report_translation || enMaster.ui.btn_report_translation}
            </a>
            <SupportHelpButton
              label={ui.help_btn || RENEWAL_PAGE_EN.help_btn}
              onClick={() => setShowSupportModal(true)}
              style={{
                fontSize: '0.78rem',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            />
          </div>
        </div>
      </div>
    </header>
  );

  const handleSubmit = async () => {
    setNotice(null);
    if (!schoolId) return;
    if (!validate()) {
      setNotice({ type: 'err', text: ui.error_validation });
      return;
    }
    if (!country) {
      setNotice({ type: 'err', text: ui.error_country });
      return;
    }

    if (preview) {
      setNotice({
        type: 'ok',
        text: 'Preview only. On the live page this would save the request and open payment.',
      });
      return;
    }

    setBusy(true);
    try {
      const submitDetails = {
        submitterName: submitterName.trim(),
        submissionDate,
        consentInstitutionalAuthority: cAuth,
        consentAccuracy: cAcc,
        consentDataProcessing: cData,
      };
      const quotedFee = feeServerQuote?.feeGbp ?? feeServerQuote?.fee ?? feeDerived?.amount ?? null;

      const init = await adminServices.startRenewalOrUpgradeRequest({
        schoolId,
        requestType,
        targetLevel,
        billingLevel: billingLevelKey(targetLevel),
        feeGbp: quotedFee,
        schoolName: officialName,
        actorName: submitterName.trim(),
        submitDetails,
      });

      const functions = getFunctions();
      const origin =
        typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'https://accreditation.ifip.group';
      const payload = {
        schoolId,
        country,
        level: billingLevelKey(targetLevel),
        officialName,
        officialEmail,
        source: 'hub_renewal_flow',
        requestType: init.requestType,
        cycleRef: init.cycleRef,
        appliedLevel: init.appliedLevel,
        successUrl: `${origin}/renewal?sid=${encodeURIComponent(schoolId)}&payment=success`,
        cancelUrl: `${origin}/renewal?sid=${encodeURIComponent(schoolId)}&canceled=true`
      };

      if (paymentMode === 'partner') {
        await adminServices.startRenewalWithPartnerOffer({
          schoolId,
          country,
          level: billingLevelKey(targetLevel),
          offerCode: offerCode.trim(),
          cycleRef: init.cycleRef,
          requestType: init.requestType,
          actorName: submitterName.trim(),
          submitDetails,
        });
        setNotice({ type: 'ok', text: ui.success_partner });
      } else if (paymentChoice === 'invoice') {
        const createInvoice = httpsCallable(functions, 'createStripeInvoice');
        await createInvoice(payload);
        setNotice({ type: 'ok', text: ui.success_invoice });
      } else {
        const createCheckout = httpsCallable(functions, 'createStripeCheckout');
        const res = await createCheckout(payload);
        const url = res?.data?.url;
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
        setNotice({ type: 'ok', text: ui.success_checkout });
      }
    } catch (err) {
      setNotice({ type: 'err', text: err?.message || String(err) });
    } finally {
      setBusy(false);
    }
  };

  if (!schoolId) {
    return (
      <Fragment>
        <div className="audit-portal renewal-page" dir={dir} style={{ minHeight: '100vh', padding: '72px 24px 40px', color: 'var(--a11y-page-text)' }}>
          <div className="renewal-page-chrome-top renewal-page-chrome-top--floating">
            <AccessibilityControls
              panelOpen={a11yPanelOpen}
              setPanelOpen={setA11yPanelOpen}
              scheme={a11yScheme}
              setScheme={setA11yScheme}
              labels={pickRenewalA11yLabels()}
              rootClassName="ifip-a11y-control-root ifip-a11y-control-root--splash-inline-toolbar"
              panelId="a11y-panel-renewal-nosid"
              accessibilityTriggerIconOnly
              splashLangSelect={
                <select
                  className="ifip-splash-lang-select"
                  value={langKey}
                  onChange={(e) => {
                    const next = e.target.value;
                    setLangKey(next);
                    localStorage.setItem('ifip_lang', next);
                  }}
                  aria-label={renewalSplashLangAria}
                >
                  {Object.entries(languageManifest)
                    .sort((a, b) =>
                      (a[1].nativeName || a[1].label || a[0]).localeCompare(
                        b[1].nativeName || b[1].label || b[0]
                      )
                    )
                    .map(([k, lang]) => (
                      <option key={k} value={k}>
                        {lang.nativeName}
                      </option>
                    ))}
                </select>
              }
            />
          </div>
          <div style={{ maxWidth: 560, margin: '0 auto' }}>
            <img
              src="/images/01%20IFIP%20Logo.png"
              alt={portalUi.img_alt_ifip || enMaster.ui.img_alt_ifip || 'IFIP'}
              style={{ height: 56, width: 'auto', marginBottom: 24, display: 'block' }}
            />
            <h1>{ui.missing_sid_title}</h1>
            <p>{ui.missing_sid_body}</p>
            <p>{ui.missing_sid_recovery_hint}</p>
            <p style={{ marginTop: 16 }}>
              <a href="/register?recovery=1" style={{ color: '#7dd3fc', marginRight: 16 }}>
                {ui.link_register_recovery}
              </a>
              <a href="/register" style={{ color: '#7dd3fc' }}>
                {ui.link_register_intro}
              </a>
            </p>
            <p style={{ marginTop: 20, opacity: 0.9 }}>
              {ui.missing_sid_contact}{' '}
              <a href={`mailto:${ui.missing_sid_contact_email}`} style={{ color: '#7dd3fc' }}>
                {ui.missing_sid_contact_email}
              </a>
              .
            </p>
          </div>
        </div>
      </Fragment>
    );
  }

  if (!profileLoaded || !schoolProfile) {
    return (
      <div className="audit-portal renewal-page" dir={dir}>
        {renderPortalHeader(schoolId)}
        <main className="renewal-page__main">
          <p className="renewal-page__lede">{ui.localizing}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="audit-portal renewal-page" dir={dir}>
      {renderPortalHeader(officialName)}

      <main className="renewal-page renewal-page__main">
        <header className="renewal-page__hero">
          <h2 className="renewal-page__hero-title">{ui.page_title}</h2>
          <p className="renewal-page__hero-intro">{ui.page_intro}</p>
        </header>

        {preview && (
          <div className="renewal-callout" style={{ marginTop: 8 }}>
            <strong>{ui.preview_banner}</strong>
            <p style={{ margin: '10px 0 0', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <a href="/renewal?preview=1&previewMarket=online">Card or invoice</a>
              <a href="/renewal?preview=1&previewMarket=partner&previewCountry=India">Partner code</a>
              <a href="/renewal?preview=1&previewMarket=closed">Level closed</a>
              <a href="/renewal?preview=1&previewMarket=online&previewFinalised=0">Cycle still open</a>
            </p>
          </div>
        )}

        {notice && (
          <div
            style={{
              marginTop: 8,
              marginBottom: 8,
              padding: 14,
              borderRadius: 10,
              border: `1px solid ${notice.type === 'err' ? '#f87171' : '#4ade80'}`,
              background: notice.type === 'err' ? 'rgba(248,113,113,0.12)' : 'rgba(74,222,128,0.12)',
              fontSize: '0.95rem'
            }}
          >
            {notice.text}
          </div>
        )}

        <section className="renewal-section">
          <h2 className="renewal-section__title">{ui.section_form_title}</h2>
          <p className="renewal-section__subtitle">{ui.section_form_intro}</p>

          <div className="renewal-field-row renewal-field-row--intent">
            <label className="renewal-label renewal-label--full">
              <span className="renewal-label__text">
                {ui.renewal_intent_label}
                <span className="renewal-asterisk" aria-hidden="true">
                  *
                </span>
              </span>
              <select
                value={intent}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'renew') {
                    setIntent('renew');
                    setTargetLevel(currentLevelKey);
                  } else {
                    setIntent('upgrade');
                    setTargetLevel(currentLevelKey === 'foundation' ? 'certified' : 'global');
                  }
                }}
                aria-required="true"
              >
                <option value="renew">{renewOptionLabel}</option>
                {canUpgrade && <option value="upgrade">{ui.upgrade_my_accreditation}</option>}
              </select>
            </label>
            {intent === 'upgrade' && canUpgrade && (
              <label className="renewal-label renewal-label--full">
                <span className="renewal-label__text">
                  {ui.target_level_label}
                  <span className="renewal-asterisk" aria-hidden="true">
                    *
                  </span>
                </span>
                <select value={targetLevel} onChange={(e) => setTargetLevel(e.target.value)} aria-required="true">
                  {higherLevels.map((level) => (
                    <option key={level} value={level}>
                      {levelDisplayName(level, ui)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {!cycleComplete && (
            <div className="renewal-callout renewal-callout--blocked" style={{ marginTop: 20 }}>
              <strong style={{ fontSize: '1rem' }}>{ui.renewal_blocked_title}</strong>
              <p style={{ margin: '10px 0 0' }}>{ui.renewal_blocked_body}</p>
            </div>
          )}

          {cycleComplete && (
          <>
          <div className="renewal-field-row" style={{ marginTop: 24 }}>
            <label className="renewal-label">
              <span className="renewal-label__text">
                {ui.label_submitter_name}
                <span className="renewal-asterisk" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                type="text"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                required
                autoComplete="name"
              />
            </label>
            <label className="renewal-label">
              <span className="renewal-label__text">
                {ui.label_submission_date}
                <span className="renewal-asterisk" aria-hidden="true">
                  *
                </span>
              </span>
              <input type="date" value={submissionDate} onChange={(e) => setSubmissionDate(e.target.value)} required />
            </label>
          </div>

          <div className="renewal-consent-block">
            <label>
              <input type="checkbox" checked={cAuth} onChange={(e) => setCAuth(e.target.checked)} />
              <span>
                <strong>{ui.consent_q21_label}</strong>
                <span className="renewal-asterisk" aria-hidden="true">
                  *
                </span>
                <br />
                <span style={{ opacity: 0.88, fontWeight: 400 }}>{ui.consent_q21_desc}</span>
              </span>
            </label>
            <label>
              <input type="checkbox" checked={cAcc} onChange={(e) => setCAcc(e.target.checked)} />
              <span>
                <strong>{ui.consent_q22_label}</strong>
                <span className="renewal-asterisk" aria-hidden="true">
                  *
                </span>
                <br />
                <span style={{ opacity: 0.88, fontWeight: 400 }}>{ui.consent_q22_desc}</span>
              </span>
            </label>
            <label>
              <input type="checkbox" checked={cData} onChange={(e) => setCData(e.target.checked)} />
              <span>
                <strong>{ui.consent_q23_label}</strong>
                <span className="renewal-asterisk" aria-hidden="true">
                  *
                </span>
                <br />
                <span style={{ opacity: 0.88, fontWeight: 400 }}>{ui.consent_q23_desc}</span>
              </span>
            </label>
          </div>
          </>
          )}
        </section>

        {cycleComplete && (
        <section className="renewal-section">
          <h2 className="renewal-section__title">{ui.payment_section_title}</h2>
          <p className="renewal-fee-line">
            <strong>{ui.fee_label}</strong>{' '}
            {!country || !pricingConfig
              ? ui.fee_unavailable
              : !preview && feeDerived && feeDerived.available === false
                ? ui.fee_bespoke
                : feeServerQuote?.displayLine
                  ? feeServerQuote.displayLine
                  : feeDerived != null
                    ? `£${Number(feeDerived.amount).toLocaleString('en-GB')}`
                    : ui.fee_unavailable}
          </p>

          {paymentMode === 'partner' && (
            <div className="renewal-callout" style={{ marginTop: 16 }}>
              <strong>{ui.partner_pay_title}</strong>
              <p style={{ margin: '10px 0' }}>
                {fillRenewalTemplate(
                  partnerName ? ui.partner_pay_body : ui.partner_pay_body_no_name,
                  { country: countryLabel, partnerName }
                )}
              </p>
              <label className="renewal-label">
                <span className="renewal-label__text">
                  {partnerName
                    ? fillRenewalTemplate(ui.partner_code_label_named, { partnerName })
                    : ui.partner_code_label}
                  <span className="renewal-asterisk" aria-hidden="true">*</span>
                </span>
                <input
                  type="text"
                  value={offerCode}
                  onChange={(e) => setOfferCode(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                />
              </label>
            </div>
          )}

          {paymentMode === 'online' && (
            <div className="renewal-payment-method">
              <p style={{ margin: '12px 0' }}>
                {fillRenewalTemplate(ui.online_pay_body, { country: countryLabel })}
              </p>
              <span className="renewal-payment-method__label">
                {ui.payment_method_label}
                <span className="renewal-asterisk" aria-hidden="true">*</span>
              </span>
              <select value={paymentChoice} onChange={(e) => setPaymentChoice(e.target.value)} aria-required="true">
                <option value="card">{ui.payment_card}</option>
                <option value="invoice">{ui.payment_invoice}</option>
              </select>
            </div>
          )}

          {paymentMode === 'closed' && (
            <div className="renewal-callout renewal-callout--blocked" style={{ marginTop: 16 }}>
              <p style={{ margin: 0 }}>{ui.closed_market_body}</p>
            </div>
          )}

          {paymentMode !== 'closed' && (
            <button type="button" className="btn-primary-action" onClick={handleSubmit} disabled={busy || !validate()}>
              {busy ? ui.btn_working : paymentMode === 'partner' ? ui.btn_confirm_code : ui.btn_continue_payment}
            </button>
          )}
        </section>
        )}
      </main>
      <SupportIssueModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        sourceSurface="renewal_page"
        context={supportContext}
      />
    </div>
  );
};

export default RenewalUpgradePage;
